import Tour from '@core/components/Tour';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { registroCliente } from '@core/util/mockTourUtils';
import { steps } from '@core/util/tourUtils.js';
import FusePageCarded from '@fuse/core/FusePageCarded';
import { excluirAlert } from 'app/services/alerts';
import { clienteApi } from 'app/services/api/clienteApi';
import { usuarioApi } from 'app/services/api/usuarioApi';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext';
import { eventReactGA, initializeReactGA } from 'app/utils/analyticsUtil';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { GrupoClienteProvider } from '../grupocliente/GrupoClienteContext';
import GrupoClienteModal from '../grupocliente/GrupoClienteModal';
import { ClienteContext, ClienteProvider } from './ClienteContext';
import { ClienteSearchHeader, ClienteTable } from './components';
import { CadastroClienteModal, ImportarClienteModal } from './components/modal';
import ClienteBuscaAvancadaModal from './components/modal/ClienteBuscaAvancadaModal';
import VisualizarClienteModal from './components/modal/VisualizarClienteModal';

const CLIENTE = 'Cliente';

const Cliente = () => {
  const [, setSelecao] = useContext(ClienteContext).selecao;
  const [, setTipoSelecao] = useContext(ClienteContext).tipoSelecao;
  const [, setClientes] = useContext(ClienteContext).clientes;
  const [, setClienteAtual] = useContext(ClienteContext).clienteAtual;
  const [, setTotalElements] = useContext(ClienteContext).totalElements;
  const [page, setPage] = useContext(ClienteContext).page;
  const [size, setSize] = useContext(ClienteContext).size;
  const [sort, setSort] = useContext(ClienteContext).sort;
  const [loading, setLoading] = useContext(ClienteContext).loading;
  const [filtroAvancado] = useContext(ClienteContext).filtroAvancado;
  const [search] = useContext(ClienteContext).search;
  const [funcionalidadesTour, setFuncionalidadesTour] = useContext(GlobalContext).funcionalidadesTour;

  const [run, setRun] = useState(false);
  const [tour, setTour] = useState(false);

  const history = useHistory();

  const dispatch = useDispatch();

  useReduxLoading(loading);

  const apiUtils = useApiUtils(dispatch, Actions);

  useEffect(() => {
    initializeReactGA(history?.location?.pathname);
    if (!funcionalidadesTour) {
      setFuncionalidadesTour([]);
    }
    buscarTour();
    // Efeito de montagem: incluir as dependências reexecutaria a carga inicial a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  const resetSelecao = () => {
    setSelecao([]);
    setTipoSelecao('NENHUM');
  };

  const initialPage = {
    page,
    q: '',
    size,
    sort,
  };

  const buscarTour = async () => {
    const funcionalidadeJaExistente = funcionalidadesTour?.some((e) => e === 'CLIENTES');
    if (!funcionalidadeJaExistente) {
      setRun(true);
      setTour(true);
      setPage(registroCliente.number);
      setSize(registroCliente.size);
      setSort(registroCliente.sort?.replace(': ', ','));
      setTotalElements(registroCliente.totalElements);
      setClientes(registroCliente.content);
    } else {
      fetchClientes();
    }
  };

  const fetchCliente = async (clienteId) => {
    setLoading(true);
    try {
      const response = await clienteApi.getOne(clienteId);
      setClienteAtual(response.data);
    } catch (err) {
      apiUtils.handleApiError(err);
    }
    setLoading(false);
  };

  const fetchClientes = async (search = initialPage) => {
    setLoading(true);
    try {
      if (!search?.filtroAvancado) {
        search.filtroAvancado = filtroAvancado;
      }
      const response = await clienteApi.get(search);
      setSelecao([]);
      setPage(response.data.number);
      setSize(response.data.size);
      setSort(response.data.sort?.replace(': ', ','));
      setTotalElements(response.data.totalElements);
      setClientes(response.data.content);
    } catch (err) {
      apiUtils.handleApiError(err);
    }
    setLoading(false);
  };

  const handleSelectionChange = (selecao) => {
    setSelecao(selecao);
  };

  const handleUploadImage = async (image) => {
    try {
      const res = await clienteApi.uploadImage(image);

      return { success: true, data: res.data };
    } catch (err) {
      const error = apiUtils.handleApiError(err);
      return { success: false, error };
    }
  };

  const handleSubmit = async (_cliente, pageable) => {
    let res;
    let message;

    setLoading(true);

    try {
      if (_cliente?.id) {
        eventReactGA(CLIENTE, 'Atualizar cliente');
        message = 'Cliente editado com sucesso';
        res = await clienteApi.put(_cliente);
      } else {
        eventReactGA(CLIENTE, 'Cadastrar cliente');
        message = 'Cliente salvo com sucesso';
        res = await clienteApi.post(_cliente);
      }

      showSuccess(message);
      resetSelecao();
      fetchClientes({ q: search, page, size, sort, filtroAvancado });
    } catch (err) {
      apiUtils.handleApiError(err);
    }

    setLoading(false);

    return res;
  };

  const handleDelete = (filtro) => {
    excluirAlert({
      confirmar: async () => {
        try {
          setLoading(true);
          eventReactGA(CLIENTE, 'Excluir cliente');
          await clienteApi.delete({ ...filtro, ids: filtro.ids.map((c) => c.id) });
          showSuccess('Cliente excluído com sucesso.');
          resetSelecao();
          fetchClientes({ q: search, page, size, sort, filtroAvancado });
        } catch (err) {
          apiUtils.handleApiError(err);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const atualizarTour = async () => {
    try {
      setLoading(true);
      funcionalidadesTour.push('CLIENTES');
      await usuarioApi.updateTour(funcionalidadesTour);
      setRun(false);
      setTour(false);
      setFuncionalidadesTour(funcionalidadesTour);
      fetchClientes();
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GrupoClienteProvider>
      <Tour steps={steps} run={run} setRun={setRun} onFinish={() => atualizarTour()} disableScrolling={true} />
      <FusePageCarded
        innerScroll
        header={<ClienteSearchHeader />}
        content={
          <>
            <ClienteTable
              onSelection={handleSelectionChange}
              fetchCliente={fetchCliente}
              fetchClientes={fetchClientes}
              onDelete={handleDelete}
              onTour={tour}
            />
            <CadastroClienteModal onSubmit={handleSubmit} onUploadImage={handleUploadImage} fetchPapel={fetchCliente} />
            <GrupoClienteModal />
            <ImportarClienteModal fetchClientes={fetchClientes} />
            <VisualizarClienteModal />
            <ClienteBuscaAvancadaModal fetchClientes={fetchClientes} />
          </>
        }
      />
    </GrupoClienteProvider>
  );
};

const ClienteContainer = () => (
  <ClienteProvider>
    <Cliente />
  </ClienteProvider>
);

export default ClienteContainer;
